import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Radio,
  Grid,
  IconButton,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReferralData } from './types';
import { DeduplicationMatch } from './Table/DeduplicationTable';
import { RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { applyDateFormat, formatDateCell } from './utils';
import { useGetTrackingParametersByEnrollmentIdQuery } from '../../features/lma/ccPatientApi';
import { mapEnrollmentEthnicity } from './utils/ethnicityUtils';

interface MergeComparisonModalProps {
  open: boolean;
  onClose: () => void;
  sourceRecord: ReferralData;
  targetRecord: DeduplicationMatch | null;
  onMerge: (
    selectedFields: string[],
    setSelectedFields: (fields: string[]) => void,
    onClose: () => void,
    confirmationModalMessages?: any,
    trackingParamsData?: any,
    targetRecord?: any
  ) => void;
  confirmationModalMessages?: any;
  isSending?: boolean;
}

interface ComparisonField {
  key: string;
  sitecoreKey?: string;
  label: string;
  sourceValue: string;
  targetValue: string;
}

const MergeComparisonModal: React.FC<MergeComparisonModalProps> = ({
  open,
  onClose,
  sourceRecord,
  targetRecord,
  onMerge,
  confirmationModalMessages,
  isSending = false,
}) => {
  const { sitecoreContext } = useSitecoreContext();
  const dateFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateFormat?.value || 'MM/DD/YYYY';

  const { data: trackingParamsData } = useGetTrackingParametersByEnrollmentIdQuery(
    sourceRecord.enrollmentId,
    { skip: !sourceRecord.enrollmentId }
  );

  // Helper to get source UTM value from API response (returns comma-joined string or '-')
  const getSourceTrackingParam = (key: string): string => {
    if (!trackingParamsData) return (sourceRecord[key as keyof ReferralData] as string) || '-';
    const apiKey = key;
    const val = apiKey ? trackingParamsData[apiKey] : undefined;
    if (!val) return '-';
    if (typeof val === 'string') return val || '-';
    if (Array.isArray(val)) return val.filter(Boolean).join(', ') || '-';
    return '-';
  };

  // Check if source UTM value already exists in CC (don't send duplicates)
  const shouldDisableUtmField = (sourceVal: string, targetVal: string): boolean => {
    if (!sourceVal || sourceVal === '-' || !targetVal || targetVal === '-') return false;
    const sourceValues = sourceVal
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const targetValues = targetVal
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return sourceValues.every((sv) => targetValues.includes(sv));
  };

  const getMatchingPhoneNumber = (): string => {
    const cleanSourcePhone = sourceRecord.phone?.replace(/\D/g, '') || '';

    if (!cleanSourcePhone || !targetRecord) {
      return '-';
    }

    const formatDigits = (digits: string): string =>
      digits.length === 10
        ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
        : digits;

    const phoneFields = ['phone1', 'phone2', 'phone3', 'phone4'];
    for (const phoneField of phoneFields) {
      const phoneObj = (targetRecord as any)[phoneField];
      const candidates = [phoneObj?.rawNumber, phoneObj?.number].filter(Boolean);
      for (const raw of candidates) {
        const clean = (raw as string).replace(/\D/g, '');
        if (clean === cleanSourcePhone) {
          return formatDigits(clean);
        }
      }
    }

    for (const phoneField of phoneFields) {
      const phoneObj = (targetRecord as any)[phoneField];
      if (phoneObj?.number) {
        const digits = phoneObj.number.replace(/\D/g, '');
        if (digits.length === 10) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phoneObj.number;
      }
    }

    return '-';
  };

  const formatNumber = (phoneObj: any): string => {
    const digits = phoneObj.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits || '-';
  };

  const getCCEthnicity = (): string => {
    const val = (targetRecord as any)?.ethnicity || (targetRecord as any)?.ethnicityCode || '';
    return val || '-';
  };
  const getCCRace = (): string => {
    const val = (targetRecord as any)?.race || (targetRecord as any)?.raceCode || '';
    return val || '-';
  };

  // Helper function to get UTM field value from targetRecord customFields
  const getCustomFieldValue = (fieldName: string): string => {
    const customFields = (targetRecord as any)?.customFields;
    if (!customFields || !Array.isArray(customFields)) {
      return '-';
    }
    const field = customFields.find((f: any) => f.name === fieldName);
    return field?.value || '-';
  };

  const getConsentCustomFieldValue = (fieldName: string): string => {
    const value = getCustomFieldValue(fieldName);
    return value === '-' ? '-' : String(value);
  };

  const normalizeConsentDisplay = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const formatDateOfBirth = (dateString: string | undefined): string => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const format = dateFormat || 'MM-DD-YYYY';
      return applyDateFormat(date, format);
    } catch (error) {
      return dateString;
    }
  };

  const getInitialSelectedFields = () => {
    const fields: string[] = [];
    const allFields: ComparisonField[] = [
      {
        key: 'firstName',
        label: 'First Name',
        sourceValue: sourceRecord.firstName || '-',
        targetValue: targetRecord?.firstName || '-',
      },
      {
        key: 'lastName',
        label: 'Last Name',
        sourceValue: sourceRecord.lastName || '-',
        targetValue: targetRecord?.lastName || '-',
      },
      {
        key: 'dob',
        label: 'Date of Birth',
        sourceValue: sourceRecord.dob || '-',
        targetValue: targetRecord?.dateOfBirth || '-',
      },
      {
        key: 'gender',
        label: 'Gender',
        sourceValue: sourceRecord.gender || '-',
        targetValue:
          targetRecord?.genderCode === 'M'
            ? 'Male'
            : targetRecord?.genderCode === 'F'
              ? 'Female'
              : '-',
      },
      {
        key: 'phoneNumber',
        label: 'Phone Number',
        sourceValue: formatNumber(sourceRecord.phone || ''),
        targetValue: getMatchingPhoneNumber(),
      },
      {
        key: 'email',
        label: 'Email',
        sourceValue: (sourceRecord.email || '-').toLowerCase(),
        targetValue: (targetRecord?.primaryEmail?.email || '-').toLowerCase(),
      },
      {
        key: 'address',
        label: 'Address',
        sourceValue: sourceRecord.address || '-',
        targetValue: targetRecord?.address1 || '-',
      },
      {
        key: 'city',
        label: 'City',
        sourceValue: sourceRecord.city || '-',
        targetValue: targetRecord?.city || '-',
      },
      {
        key: 'state',
        label: 'State',
        sourceValue: sourceRecord.stateCode || '-',
        targetValue: targetRecord?.state || '-',
      },
      {
        key: 'zipcode',
        label: 'Zipcode',
        sourceValue: sourceRecord.zipcode || '-',
        targetValue: targetRecord?.zip || '-',
      },
      {
        key: 'utm_source',
        label: 'UTM Source',
        sourceValue: getSourceTrackingParam('utmSource'),
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Source'),
      },
      {
        key: 'utm_medium',
        label: 'UTM Medium',
        sourceValue: getSourceTrackingParam('utmMedium'),
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Medium'),
      },
      {
        key: 'utm_campaign',
        label: 'UTM Campaign',
        sourceValue: getSourceTrackingParam('utmCampaign'),
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Name'),
      },
      {
        key: 'utm_term',
        label: 'UTM Term',
        sourceValue: getSourceTrackingParam('utmTerm'),
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Term'),
      },
      {
        key: 'utm_content',
        label: 'UTM Content',
        sourceValue: getSourceTrackingParam('utmContent'),
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Content'),
      },
      {
        key: 'sID',
        label: 'sID',
        sourceValue: getSourceTrackingParam('sid'),
        targetValue: getCustomFieldValue('Avacare Website - sID'),
      },
      {
        key: 'psecID',
        label: 'psecID',
        sourceValue: getSourceTrackingParam('psecId'),
        targetValue: getCustomFieldValue('Avacare Website - psecID'),
      },
      {
        key: 'atid',
        label: 'ATID',
        sourceValue: getSourceTrackingParam('atid'),
        targetValue: getCustomFieldValue('Avacare Website - UTM ATID'),
      },
      {
        key: 'gclid',
        label: 'GCLID',
        sourceValue: getSourceTrackingParam('gclid'),
        targetValue: getCustomFieldValue('Avacare Website - UTM GCLID'),
      },
      {
        key: 'fbclid',
        label: 'FBCLID',
        sourceValue: getSourceTrackingParam('fbclid'),
        targetValue: getCustomFieldValue('Avacare Website - UTM FBCLID'),
      },
      {
        key: 'emailOptInDate',
        label: 'Email Opt-In Date',
        sourceValue: '-',
        targetValue: '-',
      },
      {
        key: 'isSecondaryContactMediumEmail',
        label: 'Email Contact Medium',
        sourceValue: normalizeConsentDisplay(
          sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumEmail
        ),
        targetValue: getConsentCustomFieldValue(
          'Consent to Receive Calls or Emails About Future Studies'
        ),
      },
      {
        key: 'isSecondaryContactMediumSMS',
        label: 'SMS Contact Medium',
        sourceValue: normalizeConsentDisplay(
          sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumSMS
        ),
        targetValue: getConsentCustomFieldValue('Consent to Receive Texts About Future Studies'),
      },
      {
        key: 'smsOptInDate',
        label: 'SMS Opt-In Date',
        sourceValue: '-',
        targetValue: '-',
      },
      {
        key: 'ethnicity',
        sitecoreKey: 'ethnicity',
        label: 'Ethnicity',
        sourceValue: (sourceRecord as any).ethnicity || '-',
        targetValue: targetRecord?.ethnicity || '-',
      },
    ];

    allFields.forEach((field) => {
      const sourceHasValue = field.sourceValue !== '-';
      const targetHasValue = field.targetValue !== '-';
      // Auto-select fields where source has value but target doesn't (green background)
      if (field.key === 'ethnicity') {
        const enrollmentEthnicityRaw =
          (sourceRecord as ReferralData & { ethnicity?: string }).ethnicity || '';
        if (enrollmentEthnicityRaw && enrollmentEthnicityRaw !== '-') {
          const mapped = mapEnrollmentEthnicity(enrollmentEthnicityRaw);
          const ccEth = getCCEthnicity();
          const ccRace = getCCRace();
          const ccEthBlank = !ccEth || ccEth === '-';
          const ccRaceBlank = !ccRace || ccRace === '-';
          const canMoveEthnicity = !!(mapped.ethnicity && mapped.ethnicity !== '-' && ccEthBlank);
          const canMoveRace = !!(mapped.race && mapped.race !== '-' && ccRaceBlank);
          if (canMoveEthnicity || canMoveRace) {
            fields.push('ethnicity');
          }
        }
        return;
      }
      const utmKeys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'atid',
        'sID',
        'psecID',
        'gclid',
        'fbclid',
      ];
      if (
        utmKeys.includes(field.key) &&
        shouldDisableUtmField(field.sourceValue, field.targetValue)
      )
        return;
      if (sourceHasValue && !targetHasValue) {
        fields.push(field.key);
      }
    });

    // Auto-select phone if enrollment has a phone that differs from all CC phones and there's an empty slot
    const cleanEnrollmentPhone = sourceRecord.phone?.replace(/\D/g, '') || '';
    if (cleanEnrollmentPhone) {
      const ccPhoneFields = ['phone1', 'phone2', 'phone3', 'phone4'] as const;
      const ccPhoneData = ccPhoneFields.map((pf) => {
        const phoneObj = (targetRecord as any)?.[pf];
        const raw: string = phoneObj?.number || phoneObj?.rawNumber || '';
        const digits = raw.replace(/\D/g, '');
        const isEmpty = !raw || raw.trim() === '' || digits.length === 0;
        return { digits, isEmpty };
      });
      const matchesAnyCC = ccPhoneData.some((p) => p.digits === cleanEnrollmentPhone);
      const hasEmptySlot = ccPhoneData.some((p) => p.isEmpty);
      if (!matchesAnyCC && hasEmptySlot) {
        fields.push('phoneNumber');
      }
    }

    return fields;
  };

  const [selectedFields, setSelectedFields] = useState<string[]>(getInitialSelectedFields());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationMergeModalOpen, setConfirmationMergeModalOpen] = useState(false);

  const fieldLabelMap: Record<string, string> = {};
  const fieldDisabledMap: Record<string, boolean> = {};
  const fieldTooltipMap: Record<string, string> = {};
  const allowedFieldKeys = new Set<string>();
  if (
    Array.isArray(confirmationModalMessages?.MergeModalFields) &&
    confirmationModalMessages.MergeModalFields.length > 0
  ) {
    (confirmationModalMessages.MergeModalFields as any[]).forEach((item: any) => {
      const key = item?.fields?.SendToCCFieldKey?.value;
      const label = item?.fields?.SendToCCFieldLabel?.value;
      const disabled = item?.fields?.DisabledCheckbox?.value;
      const tooltip = item?.fields?.TooltipText?.value;
      if (key) {
        allowedFieldKeys.add(key);
        if (label) fieldLabelMap[key] = label;
        if (disabled) fieldDisabledMap[key] = true;
        if (tooltip) fieldTooltipMap[key] = tooltip;
      }
    });
  }

  const isValidPhone = (val: string): boolean => {
    if (!val || val.trim() === '' || /^<.*>$/.test(val.trim())) return false;
    return val.replace(/\D/g, '').length > 0;
  };

  const defaultPhoneLabels: Record<string, string> = {
    phone1: 'Mobile',
    phone2: 'Mobile 2',
    phone3: 'Phone',
    phone4: 'Phone 2',
  };

  const phoneLabelOverrides = (() => {
    const raw: string = confirmationModalMessages?.PhoneNumberLabel?.value || '';
    if (!raw.trim()) return defaultPhoneLabels;
    const parts = raw.split(',').map((s: string) => s.trim());
    return {
      phone1: parts[0] || defaultPhoneLabels.phone1,
      phone2: parts[1] || defaultPhoneLabels.phone2,
      phone3: parts[2] || defaultPhoneLabels.phone3,
      phone4: parts[3] || defaultPhoneLabels.phone4,
    };
  })();

  const getCCPhones = (): {
    key: string;
    label: string;
    value: string;
    isEmpty: boolean;
    matchesSource: boolean;
  }[] => {
    const phoneFields = ['phone1', 'phone2', 'phone3', 'phone4'] as const;
    const cleanSource = sourceRecord.phone?.replace(/\D/g, '') || '';
    return phoneFields.map((pf) => {
      const label = phoneLabelOverrides[pf] || fieldLabelMap[pf] || defaultPhoneLabels[pf];
      const phoneObj = (targetRecord as any)?.[pf];
      const raw: string = phoneObj?.number || phoneObj?.rawNumber || '';
      if (!isValidPhone(raw)) {
        return { key: pf, label, value: '', isEmpty: true, matchesSource: false };
      }
      const digits = raw.replace(/\D/g, '');
      const formatted =
        digits.length === 10
          ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
          : raw;
      const matchesSource = cleanSource.length > 0 && digits === cleanSource;
      return { key: pf, label, value: formatted, isEmpty: false, matchesSource };
    });
  };

  const ccPhones = getCCPhones();

  const enrollmentMatchesCC = ccPhones.some((p) => p.matchesSource);

  const firstEmptyPhoneKey = ccPhones.find((p) => p.isEmpty)?.key ?? '';

  const [selectedPhoneKey, setSelectedPhoneKey] = useState<string>(firstEmptyPhoneKey || 'phone1');

  const allComparisonFields: ComparisonField[] = [
    {
      key: 'firstName',
      sitecoreKey: 'firstName',
      label: fieldLabelMap['firstName'] || 'First Name',
      sourceValue: sourceRecord.firstName || '-',
      targetValue: targetRecord?.firstName || '-',
    },
    {
      key: 'lastName',
      sitecoreKey: 'lastName',
      label: fieldLabelMap['lastName'] || 'Last Name',
      sourceValue: sourceRecord.lastName || '-',
      targetValue: targetRecord?.lastName || '-',
    },
    {
      key: 'dob',
      sitecoreKey: 'dateOfBirth',
      label: fieldLabelMap['dateOfBirth'] || 'Date of Birth',
      sourceValue: formatDateCell(sourceRecord?.dob, dateFormat) || '-',
      targetValue: formatDateOfBirth(targetRecord?.dateOfBirth),
    },
    {
      key: 'gender',
      sitecoreKey: 'gender',
      label: fieldLabelMap['gender'] || 'Gender',
      sourceValue: sourceRecord.gender || '-',
      targetValue:
        targetRecord?.genderCode === 'M'
          ? 'Male'
          : targetRecord?.genderCode === 'F'
            ? 'Female'
            : '-',
    },
    {
      key: 'phoneNumber',
      sitecoreKey: 'phone',
      label: fieldLabelMap['phone'] || 'Phone Number',
      sourceValue: formatNumber(sourceRecord.phone || ''),
      targetValue: getMatchingPhoneNumber(),
    },
    {
      key: 'email',
      sitecoreKey: 'primaryEmail',
      label: fieldLabelMap['primaryEmail'] || 'Email',
      sourceValue: (sourceRecord.email || '-').toLowerCase(),
      targetValue: (targetRecord?.primaryEmail?.email || '-').toLowerCase(),
    },
    {
      key: 'address',
      sitecoreKey: 'street',
      label: fieldLabelMap['street'] || 'Address',
      sourceValue: sourceRecord.address || '-',
      targetValue: targetRecord?.address1 || '-',
    },
    {
      key: 'city',
      sitecoreKey: 'city',
      label: fieldLabelMap['city'] || 'City',
      sourceValue: sourceRecord.city || '-',
      targetValue: targetRecord?.city || '-',
    },
    {
      key: 'state',
      sitecoreKey: 'state',
      label: fieldLabelMap['state'] || 'State',
      sourceValue: sourceRecord.stateCode || '-',
      targetValue: targetRecord?.state || '-',
    },
    {
      key: 'zipcode',
      sitecoreKey: 'zipCode',
      label: fieldLabelMap['zipCode'] || 'Zipcode',
      sourceValue: sourceRecord.zipcode || '-',
      targetValue: targetRecord?.zip || '-',
    },
    {
      key: 'utm_source',
      sitecoreKey: 'utmSource',
      label: fieldLabelMap['utmSource'] || 'UTM Source',
      sourceValue: getSourceTrackingParam('utmSource'),
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Source'),
    },
    {
      key: 'utm_medium',
      sitecoreKey: 'utmMedium',
      label: fieldLabelMap['utmMedium'] || 'UTM Medium',
      sourceValue: getSourceTrackingParam('utmMedium'),
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Medium'),
    },
    {
      key: 'utm_campaign',
      sitecoreKey: 'utmCampaign',
      label: fieldLabelMap['utmCampaign'] || 'UTM Campaign',
      sourceValue: getSourceTrackingParam('utmCampaign'),
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Name'),
    },
    {
      key: 'utm_term',
      sitecoreKey: 'utmTerm',
      label: fieldLabelMap['utmTerm'] || 'UTM Term',
      sourceValue: getSourceTrackingParam('utmTerm'),
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Term'),
    },
    {
      key: 'utm_content',
      sitecoreKey: 'utmContent',
      label: fieldLabelMap['utmContent'] || 'UTM Content',
      sourceValue: getSourceTrackingParam('utmContent'),
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Content'),
    },
    {
      key: 'sID',
      sitecoreKey: 'sid',
      label: fieldLabelMap['sid'] || 'sID',
      sourceValue: getSourceTrackingParam('sid'),
      targetValue: getCustomFieldValue('Avacare Website - sID'),
    },
    {
      key: 'psecID',
      sitecoreKey: 'psecId',
      label: fieldLabelMap['psecId'] || 'PSEC ID',
      sourceValue: getSourceTrackingParam('psecId'),
      targetValue: getCustomFieldValue('Avacare Website - psecID'),
    },
    {
      key: 'atid',
      sitecoreKey: 'atid',
      label: fieldLabelMap['atid'] || 'AT ID',
      sourceValue: getSourceTrackingParam('atid'),
      targetValue: getCustomFieldValue('Avacare Website - UTM ATID'),
    },
    {
      key: 'gclid',
      sitecoreKey: 'gclid',
      label: fieldLabelMap['gclid'] || 'Google Click ID',
      sourceValue: getSourceTrackingParam('gclid'),
      targetValue: getCustomFieldValue('Avacare Website - UTM GCLID'),
    },
    {
      key: 'fbclid',
      sitecoreKey: 'fbclid',
      label: fieldLabelMap['fbclid'] || 'Facebook Click ID',
      sourceValue: getSourceTrackingParam('fbclid'),
      targetValue: getCustomFieldValue('Avacare Website - UTM FBCLID'),
    },
    {
      key: 'emailOptInDate',
      sitecoreKey: 'emailOptInDate',
      label: fieldLabelMap['emailOptInDate'] || 'Email Opt-In Date',
      sourceValue: '-',
      targetValue: '-',
    },
    {
      key: 'isSecondaryContactMediumEmail',
      sitecoreKey: 'isSecondaryContactMediumEmail',
      label: fieldLabelMap['isSecondaryContactMediumEmail'] || 'Email Contact Medium',
      sourceValue: normalizeConsentDisplay(
        sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumEmail
      ),
      targetValue: getConsentCustomFieldValue(
        'Consent to Receive Calls or Emails About Future Studies'
      ),
    },
    {
      key: 'isSecondaryContactMediumSMS',
      sitecoreKey: 'isSecondaryContactMediumSMS',
      label: fieldLabelMap['isSecondaryContactMediumSMS'] || 'SMS Contact Medium',
      sourceValue: normalizeConsentDisplay(
        sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumSMS
      ),
      targetValue: getConsentCustomFieldValue('Consent to Receive Texts About Future Studies'),
    },
    {
      key: 'smsOptInDate',
      sitecoreKey: 'smsOptInDate',
      label: fieldLabelMap['smsOptInDate'] || 'SMS Opt-In Date',
      sourceValue: '-',
      targetValue: '-',
    },
    {
      key: 'ethnicity',
      sitecoreKey: 'ethnicity',
      label: fieldLabelMap['ethnicity'] || 'Ethnicity',
      sourceValue: (sourceRecord as any).ethnicity || '-',
      targetValue: targetRecord?.ethnicity || '-',
    },
  ];

  const comparisonFields: ComparisonField[] =
    allowedFieldKeys.size > 0
      ? allComparisonFields.filter((f) => allowedFieldKeys.has(f.sitecoreKey ?? f.key))
      : allComparisonFields;

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((key) => key !== fieldKey) : [...prev, fieldKey]
    );
  };

  const getEnrichedSelectedFields = (): string[] => {
    let result = [...selectedFields];

    if (result.includes('phoneNumber')) {
      const chosenPhone = ccPhones.find((p) => p.key === selectedPhoneKey);
      const compound = `phoneNumber:${selectedPhoneKey}:${chosenPhone?.label ?? selectedPhoneKey}`;
      result = result.map((k) => (k === 'phoneNumber' ? compound : k));
    }

    if (result.includes('ethnicity')) {
      const enrollmentEthnicityRaw = (sourceRecord as any).ethnicity || '';
      const mapped = mapEnrollmentEthnicity(enrollmentEthnicityRaw);
      const ccEth = getCCEthnicity();
      const ccRace = getCCRace();
      const ccEthBlank = !ccEth || ccEth === '-';
      const ccRaceBlank = !ccRace || ccRace === '-';
      // Only send a value for each field if the CC slot is actually empty — never overwrite existing CC data
      const ethToSend = ccEthBlank ? mapped.ethnicity || '' : '';
      const raceToSend = ccRaceBlank ? mapped.race || '' : '';
      const compound = `ethnicity:${ethToSend}|${raceToSend}`;
      result = result.map((k) => (k === 'ethnicity' ? compound : k));
    }

    return result;
  };

  const handleConfirmMerge = () => {
    onMerge(
      getEnrichedSelectedFields(),
      setSelectedFields,
      confirmationModalMessages,
      onClose,
      trackingParamsData,
      targetRecord
    );
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setSelectedFields([]);
    setConfirmDialogOpen(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 0.75, px: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
            {confirmationModalMessages?.MergeHeaderText?.value || `Merge Comparison - CDP Profile`}
          </Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 1.5,
          height: '500px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
            <RichText
              field={
                confirmationModalMessages?.MergeModalSummary ||
                'Select fields from matched record to override in source. Checked fields will be updated during merge.'
              }
            />
          </Typography>
        </Box>

        {/* Header Row */}
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          <Grid item xs={3}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                Field
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: (theme: any) => theme.palette.primary?.light || '#E3F2FD',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                {confirmationModalMessages?.SourceHeaderText?.value || 'Source Record'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: '9px' }}
              >
                Select
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: (theme: any) => theme.palette.success?.light || '#E8F5E9',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                {(confirmationModalMessages?.TargetHeaderText?.value || 'Matched Record').replace(
                  '{CCID}',
                  targetRecord?.id ?? ''
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 0.5 }} />

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>
          {comparisonFields.map((field) => {
            const normalize = (v: string) =>
              String(v ?? '')
                .replace(/\s+/g, ' ')
                .trim();
            const normalSource = normalize(field.sourceValue);
            const normalTarget = normalize(field.targetValue);

            const sourceHasValue = normalSource !== '-';
            const targetHasValue = normalTarget !== '-';
            const isSame =
              field.key === 'email'
                ? normalSource.toLowerCase() === normalTarget.toLowerCase()
                : normalSource === normalTarget;
            const isSelected = selectedFields.includes(field.key);

            let sourceBackground = 'white';
            let showCheckbox = false;
            let isCheckboxDisabled = false;
            const checkboxTooltip = fieldTooltipMap[field?.sitecoreKey ?? field.key] || '';

            if (isSame) {
              sourceBackground = '#f5f5f5';
              showCheckbox = false;
            } else if (
              sourceHasValue &&
              targetHasValue &&
              fieldDisabledMap[field?.sitecoreKey ?? field.key]
            ) {
              sourceBackground = '#fff3cd';
              showCheckbox = true;
              isCheckboxDisabled = true;
            } else if (sourceHasValue && !targetHasValue) {
              sourceBackground = '#d4edda';
              showCheckbox = true;
              isCheckboxDisabled = !confirmationModalMessages?.EnableManualFieldSelection?.value;
            } else if (sourceHasValue && targetHasValue && !isSame) {
              sourceBackground = '#fff3cd';
              showCheckbox = true;
              isCheckboxDisabled = false;
            }

            const utmKeys = [
              'utm_source',
              'utm_medium',
              'utm_campaign',
              'utm_term',
              'utm_content',
              'atid',
              'sID',
              'psecID',
              'gclid',
              'fbclid',
            ];
            if (utmKeys.includes(field.key) && shouldDisableUtmField(normalSource, normalTarget)) {
              showCheckbox = false;
              sourceBackground = '#f5f5f5';
            }

            const targetBackground = isSame ? '#f5f5f5' : 'white';

            return (
              <Box key={field.key} sx={{ mb: 0.5 }}>
                {field.key === 'phoneNumber' ? (
                  (() => {
                    /* ── Phone-number row ── */

                    // Enrollment phone (formatted)
                    const enrollmentPhoneFormatted = normalSource;
                    const enrollmentHasValue = enrollmentPhoneFormatted !== '-';

                    // Does enrollment phone exactly match any CC phone? (digit-only comparison)
                    const exactMatchCC = enrollmentMatchesCC;

                    // Show the checkbox only when enrollment has a value AND no exact digit match
                    const showPhoneCheckbox = enrollmentHasValue && !exactMatchCC;
                    const isPhoneSelected = selectedFields.includes('phoneNumber');
                    const phoneSourceBackground = showPhoneCheckbox ? '#d4edda' : sourceBackground;

                    const noEmptySlot = !firstEmptyPhoneKey;
                    const isPhoneCheckboxDisabled = isCheckboxDisabled || noEmptySlot;

                    return (
                      <Grid container spacing={1} alignItems="flex-start">
                        {/* Field Label */}
                        <Grid item xs={3}>
                          <Box sx={{ py: 0.25, px: 0.75, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px' }}>
                              {field.label}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Enrollment (source) phone */}
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              py: 0.25,
                              px: 0.75,
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              backgroundColor: phoneSourceBackground,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                            >
                              {enrollmentPhoneFormatted}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Checkbox: only when source has value and no exact CC match */}
                        <Grid item xs={1}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            {showPhoneCheckbox && (
                              <Tooltip
                                title={checkboxTooltip}
                                disableHoverListener={!isPhoneCheckboxDisabled}
                                arrow
                              >
                                <span>
                                  <Checkbox
                                    checked={isPhoneSelected}
                                    disabled={isPhoneCheckboxDisabled}
                                    onChange={() => {
                                      const willBeSelected = !isPhoneSelected;
                                      handleFieldToggle('phoneNumber');
                                      // When checking, auto-point radio to first empty slot
                                      if (willBeSelected && firstEmptyPhoneKey) {
                                        setSelectedPhoneKey(firstEmptyPhoneKey);
                                      }
                                    }}
                                    size="small"
                                    sx={{
                                      color: (theme: any) => theme.palette.neutral5?.main,
                                      '&.Mui-checked': {
                                        color: (theme: any) =>
                                          theme.palette.themeColor?.main || '#255E5E',
                                      },
                                      p: 0,
                                      '&.Mui-disabled': {
                                        color: (theme: any) =>
                                          isPhoneCheckboxDisabled
                                            ? undefined
                                            : theme.palette.themeColor?.main || '#255E5E',
                                        opacity: 0.75,
                                      },
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Grid>

                        {/* CC phone slots – all 4 always shown; only empty slots are selectable */}
                        <Grid item xs={4}>
                          {ccPhones.map((phone) => {
                            const isEmptySlot = phone.isEmpty;
                            // Radio is disabled if slot is occupied (no overwrite allowed) or checkbox not checked
                            const radioDisabled = !isEmptySlot || !isPhoneSelected;
                            // Only show a radio as checked when the checkbox is active AND this slot is selected
                            const isChecked = isPhoneSelected && selectedPhoneKey === phone.key;

                            let slotBg = 'white';
                            if (!isEmptySlot) {
                              slotBg = '#f5f5f5'; // occupied slot
                            }

                            return (
                              <Box
                                key={phone.key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 0.4,
                                  border: `1px solid #e0e0e0`,
                                  borderRadius: 1,
                                  backgroundColor: slotBg,
                                  px: 0.5,
                                  py: 0.15,
                                  cursor: isEmptySlot && isPhoneSelected ? 'pointer' : 'default',
                                  // opacity: !isEmptySlot ? 0.9 : 1,
                                }}
                                onClick={() => {
                                  if (isEmptySlot && isPhoneSelected)
                                    setSelectedPhoneKey(phone.key);
                                }}
                              >
                                <Radio
                                  checked={isChecked}
                                  disabled={radioDisabled}
                                  onChange={() => {
                                    if (isEmptySlot && isPhoneSelected)
                                      setSelectedPhoneKey(phone.key);
                                  }}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    mr: 0.5,
                                    color: (theme: any) => theme.palette.neutral5?.main,
                                    '&.Mui-checked': {
                                      color: (theme: any) =>
                                        theme.palette.themeColor?.main || '#255E5E',
                                    },
                                    '&.Mui-disabled': { opacity: 0.4 },
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    mr: 0.5,
                                    minWidth: 52,
                                  }}
                                >
                                  {phone.label}:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    wordBreak: 'break-word',
                                    fontSize: '12px',
                                    color: phone.value ? 'text.primary' : 'text.disabled',
                                    fontStyle: phone.value ? 'normal' : 'italic',
                                  }}
                                >
                                  {phone.value || 'empty'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Grid>
                      </Grid>
                    );
                  })()
                ) : field.key === 'ethnicity' ? (
                  (() => {
                    const enrollmentEthnicityRaw = (sourceRecord as any).ethnicity || '';
                    const enrollmentHasEthnicity =
                      !!enrollmentEthnicityRaw && enrollmentEthnicityRaw !== '-';
                    const mapped = mapEnrollmentEthnicity(enrollmentEthnicityRaw);
                    const ccEthValue = getCCEthnicity();
                    const ccRaceValue = getCCRace();
                    const ccEthBlank = !ccEthValue || ccEthValue === '-';
                    const ccRaceBlank = !ccRaceValue || ccRaceValue === '-';

                    const canMoveEthnicity =
                      mapped.ethnicity && mapped.ethnicity !== '-' && ccEthBlank;
                    const canMoveRace = mapped.race && mapped.race !== '-' && ccRaceBlank;
                    const canMove = !!(canMoveEthnicity || canMoveRace);

                    const showCheckbox = enrollmentHasEthnicity && canMove;
                    const isEthnicitySelected = selectedFields.includes('ethnicity');

                    const backgroundColor = !showCheckbox ? '#f5f5f5' : '#d4edda';

                    return (
                      <Grid container spacing={1} alignItems="flex-start">
                        <Grid item xs={3}>
                          <Box sx={{ py: 0.25, px: 0.75, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px' }}>
                              {field.label}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              py: 0.25,
                              px: 0.75,
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              backgroundColor: backgroundColor,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                            >
                              {enrollmentEthnicityRaw || '-'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={1}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            {showCheckbox && (
                              <Tooltip title={checkboxTooltip} arrow>
                                <span>
                                  <Checkbox
                                    checked={isEthnicitySelected}
                                    disabled={!!fieldDisabledMap[field?.sitecoreKey ?? field.key]}
                                    onChange={() => handleFieldToggle('ethnicity')}
                                    size="small"
                                    sx={{
                                      color: (theme: any) => theme.palette.neutral5?.main,
                                      '&.Mui-checked': {
                                        color: (theme: any) =>
                                          theme.palette.themeColor?.main || '#255E5E',
                                      },
                                      '&.Mui-disabled': {
                                        color: (theme: any) =>
                                          fieldDisabledMap[field?.sitecoreKey ?? field.key]
                                            ? undefined
                                            : theme.palette.themeColor?.main || '#255E5E',
                                        opacity: 0.75,
                                      },
                                      p: 0,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          {(() => {
                            const ethSlotBg = canMoveEthnicity ? 'white' : '#f5f5f5';
                            const raceSlotBg = canMoveRace ? 'white' : '#f5f5f5';
                            return (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.4,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: ethSlotBg,
                                    px: 0.5,
                                    py: 0.15,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '11px',
                                      color: 'text.secondary',
                                      mr: 0.5,
                                      minWidth: 60,
                                    }}
                                  >
                                    Ethnicity:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: '12px', wordBreak: 'break-word' }}
                                  >
                                    {ccEthValue}
                                  </Typography>
                                </Box>
                                {canMoveEthnicity &&
                                  mapped.ethnicity &&
                                  mapped.ethnicity !== '-' && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 0.4,
                                        px: 0.5,
                                        py: 0.15,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: '11px',
                                          color: '#28a745',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        → Will set: {mapped.ethnicity}
                                        {!ccRaceBlank && ' (Race will not be overwritten)'}
                                      </Typography>
                                    </Box>
                                  )}

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.4,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: raceSlotBg,
                                    px: 0.5,
                                    py: 0.15,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '11px',
                                      color: 'text.secondary',
                                      mr: 0.5,
                                      minWidth: 60,
                                    }}
                                  >
                                    Race:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: '12px', wordBreak: 'break-word' }}
                                  >
                                    {ccRaceValue}
                                  </Typography>
                                </Box>
                                {canMoveRace && mapped.race && mapped.race !== '-' && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 0.4,
                                      px: 0.5,
                                      py: 0.15,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '11px',
                                        color: '#28a745',
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      → Will set: {mapped.race}
                                      {!ccEthBlank && ' (Ethnicity will not be overwritten)'}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            );
                          })()}
                        </Grid>
                      </Grid>
                    );
                  })()
                ) : (
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <Box sx={{ py: 0.25, px: 0.75, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px' }}>
                          {field.label}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4}>
                      <Box
                        sx={{
                          py: 0.25,
                          px: 0.75,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: sourceBackground,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                        >
                          {normalSource}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {showCheckbox && (
                          <Tooltip
                            title={checkboxTooltip}
                            disableHoverListener={
                              !(
                                sourceHasValue &&
                                targetHasValue &&
                                fieldDisabledMap[field?.sitecoreKey ?? field.key]
                              )
                            }
                            arrow
                          >
                            <span>
                              <Checkbox
                                checked={isSelected}
                                disabled={isCheckboxDisabled}
                                onChange={() => !isCheckboxDisabled && handleFieldToggle(field.key)}
                                size="small"
                                sx={{
                                  color: (theme: any) => theme.palette.neutral5?.main,
                                  '&.Mui-checked': {
                                    color: (theme: any) =>
                                      theme.palette.themeColor?.main || '#255E5E',
                                  },
                                  '&.Mui-disabled': {
                                    color: (theme: any) =>
                                      fieldDisabledMap[field?.sitecoreKey ?? field.key]
                                        ? undefined
                                        : theme.palette.themeColor?.main || '#255E5E',
                                    opacity: 0.75,
                                  },
                                  p: 0,
                                }}
                              />
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={4}>
                      <Box
                        sx={{
                          py: 0.25,
                          px: 0.75,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: targetBackground,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                        >
                          {normalTarget}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1, gap: 1, justifyContent: 'space-between' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '12px', gap: 0.5, display: 'flex', alignItems: 'center' }}
        >
          <Checkbox
            checked={confirmationMergeModalOpen}
            onChange={() => setConfirmationMergeModalOpen(!confirmationMergeModalOpen)}
            size="small"
            sx={{
              color: (theme: any) => theme.palette.neutral5?.main,
              '&.Mui-checked': {
                color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
              },
              '&.Mui-disabled': {
                color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                opacity: 0.75,
              },
              p: 0,
            }}
          />
          {confirmationModalMessages?.ConfirmationText?.value ||
            'Please review the selected fields before confirming the merge.'}
        </Typography>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleCancel} variant="outlined" size="small" color="inherit">
            {confirmationModalMessages?.CancelButtonText?.value || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            size="small"
            disabled={isSending || !confirmationMergeModalOpen}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
              '&:hover': {
                backgroundColor: (theme: any) => theme.palette.themeColor?.dark || '#1a4545',
              },
              '&.Mui-disabled': {
                backgroundColor: '#ccc',
                color: '#888',
              },
            }}
          >
            {isSending
              ? 'Merging...'
              : `${confirmationModalMessages?.MergeButtonText?.value || 'Merge TO CC'} (${
                  selectedFields.length
                })`}
          </Button>
        </div>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 0.75, px: 1.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '14px' }}>
            Confirm Merge
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '12px' }}>
            Are you sure you want to merge the selected fields to Care Coordination?
          </Typography>
          <Box
            sx={{
              p: 1,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25, fontSize: '11px' }}>
              Merge Summary:
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Source Record ID: <strong>{sourceRecord.id}</strong>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Target CC Reference: <strong>{targetRecord?.ccPatientReferenceId || '-'}</strong>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Fields to merge: <strong>{selectedFields.length}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1, gap: 0.5 }}>
          <Button onClick={handleCancelConfirm} variant="outlined" size="small" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            size="small"
            color="primary"
            sx={{
              backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
              '&:hover': {
                backgroundColor: (theme: any) => theme.palette.themeColor?.dark || '#1a4545',
              },
            }}
          >
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MergeComparisonModal;
